import { callApi } from 'src/core/callApi';
import { AI_SEARCH_API_URL } from 'src/utils/const';

export type AiSearchScanDepth = 'standard' | 'deep_research';

export type AiSearchCreateResponse = {
  message_id: string;
  status: string;
  created_at: number;
  deadline_at: number;
  search_plan?: AiSearchSearchPlan;
  answer?: string;
  metadata?: AiSearchResponseMetadata;
};

export type AiSearchSearchPlanCategory =
  | 'market_scan'
  | 'market_analysis'
  | 'property_analysis'
  | 'terms_definition'
  | 'not_supported';

export type AiSearchSearchPlan = {
  version: number;
  category: AiSearchSearchPlanCategory;
  normalized_query: string;
  market_terms?: string[];
  market_places?: Array<{
    name: string;
    latitude?: number;
    longitude?: number;
    country_code?: string;
  }>;
  micro_location?: {
    raw_phrase?: string;
    location_type?: string;
    latitude?: number;
    longitude?: number;
    radius_hint_meters?: number;
    confidence?: number;
    source?: string;
  };
  scan_market_places?: Array<{
    name: string;
    latitude?: number;
    longitude?: number;
    country_code?: string;
  }>;
  region_terms?: string[];
  scan_country_codes?: string[];
  scan_market_terms?: string[];
  filters?: {
    bedrooms?: string[] | number | null;
    bathrooms?: string[] | number | null;
    property_type?: string[] | string | null;
    amenity?: Record<string, boolean>;
    price_tier?: string | null;
    segment?: string | null;
  };
  property_context?: {
    address?: string;
    bedrooms?: number | null;
    bathrooms?: number | null;
    accommodates?: number | null;
  };
  reason?: string;
  stage1_status: 'ready' | 'unsupported';
  stage1_message: string;
  market_resolution?: {
    input_terms?: string[];
    resolved_markets?: Array<{
      market_id: number;
      name: string;
      region: string;
      country: string;
      country_code: string;
      source_term?: string;
      resolution_method?: string;
    }>;
    selection_policy?: string;
    limit?: number;
  };
  scan_depth?: AiSearchScanDepth;
  deep_dive_limit?: number;
  sort_by?: string;
  loc_group_codes?: string[];
  explorer_request?: Record<string, unknown>;
};

export type AiSearchCoordinatesFallback = {
  used: boolean;
  status?: 'sampled' | 'low_confidence' | 'no_listing_sample';
  city?: string;
  country?: string;
  latitude?: number;
  longitude?: number;
  confidence?: number;
  listing_sample_count?: number;
  total_count?: number;
  bounds?: {
    ne_lat: number;
    ne_lng: number;
    sw_lat: number;
    sw_lng: number;
  };
};

export type AiSearchFollowUpContext =
  | {
      type: 'property_bedrooms';
      property_address: string;
    }
  | {
      type: 'market_scan_depth';
      choice?: AiSearchScanDepth;
      awaiting_choice?: boolean;
    };

export type AiSearchScanManifest = {
  scan_source?: string;
  scan_depth?: AiSearchScanDepth;
  sort_by?: string;
  explorer_scope?: {
    country_codes?: string[];
    loc_group_codes?: string[];
    region_terms?: string[];
  };
  explorer_returned?: number;
  deep_dive_limit?: number;
  deep_dive_completed?: number;
  ranked_markets?: Array<{
    rank?: number;
    market_id?: number;
    name?: string;
    country?: string;
    revenue?: number;
    market_grade?: string;
  }>;
};

export type AiSearchExecutionMarketCall = {
  tool_name?: string;
  market_id: number;
  market_name?: string;
  source_term?: string;
  resolution_method?: string;
  filters?: {
    bedrooms?: string[];
    bathrooms?: string[];
    property_type?: string[];
    amenity?: Record<string, boolean>;
  };
  ok?: boolean;
  search_latitude?: number;
  search_longitude?: number;
  radius_meters?: number;
};

export type AiSearchExecutionSummary = {
  source?: 'tool_calls' | 'search_plan' | 'none';
  market_calls?: AiSearchExecutionMarketCall[];
};

export type AiSearchMicroLocationContext = {
  used?: boolean;
  raw_phrase?: string;
  location_type?: string;
  source?: string;
  confidence?: number;
  radius_hint_meters?: number;
  coordinates?: {
    latitude?: number;
    longitude?: number;
  };
  selected_radius_meters?: number;
  selected_listing_sample_count?: number;
  selected_total_count?: number;
  sample_sufficient?: boolean;
  sample_metrics_ready?: boolean;
  sample_metrics?: {
    sampled_rows?: number;
    rows_with_occupancy?: number;
    rows_with_adr?: number;
    avg_occupancy_rate?: number;
    avg_adr?: number;
  };
  radius_attempts?: Array<{
    radius_meters?: number;
    listing_sample_count?: number;
    total_count?: number;
    ok?: boolean;
  }>;
};

export type AiSearchRequestContext = {
  scan_depth?: AiSearchScanDepth;
  follow_up_context?: AiSearchFollowUpContext;
};

export type AiSearchConversationTurn = {
  role: 'user' | 'assistant';
  text: string;
};

export type AiSearchCreateRequest = {
  context?: AiSearchRequestContext;
  conversation_history?: AiSearchConversationTurn[];
  suppress_slack?: boolean;
};

export type AiSearchResponseMetadata = Record<string, unknown> & {
  coordinates_fallback_used?: boolean;
  coordinates_fallback?: AiSearchCoordinatesFallback;
  follow_up_context?: AiSearchFollowUpContext;
  execution_summary?: AiSearchExecutionSummary;
  scan_manifest?: AiSearchScanManifest;
  micro_location_context?: AiSearchMicroLocationContext;
};

export type AiSearchReadResponse = {
  message_id: string;
  status: 'queued' | 'processing' | 'completed' | 'failed' | 'failed_timeout';
  created_at: number;
  deadline_at: number;
  search_plan?: AiSearchSearchPlan;
  answer?: string;
  metadata?: AiSearchResponseMetadata;
  error?: {
    code: string;
    message: string;
  };
};

export type AiSearchApiResult<T> =
  | { ok: true; data: T }
  | { ok: false; error: string; status?: number };

import { parseAiSearchResponse } from './apiParse';

const AI_SEARCH_CREATE_PATH = `${AI_SEARCH_API_URL}/ai-search/message/create`;
const AI_SEARCH_READ_PATH = `${AI_SEARCH_API_URL}/ai-search/message/read`;

export const createAiSearchMessage = async (
  question: string,
  request?: AiSearchCreateRequest
): Promise<AiSearchApiResult<AiSearchCreateResponse>> => {
  const payload: {
    question: string;
    context?: AiSearchRequestContext;
    conversation_history?: AiSearchConversationTurn[];
    suppress_slack?: boolean;
  } = { question };
  if (request?.context) {
    payload.context = request.context;
  }
  if (request?.conversation_history && request.conversation_history.length > 0) {
    payload.conversation_history = request.conversation_history;
  }
  if (request?.suppress_slack) {
    payload.suppress_slack = true;
  }

  const response = await callApi(
    AI_SEARCH_CREATE_PATH,
    {
      body: payload,
      aiSafeMode: true,
    },
    true,
    false,
    '',
    'POST',
    true
  );

  return parseAiSearchResponse<AiSearchCreateResponse>(response);
};

export const readAiSearchMessage = async (
  messageId: string
): Promise<AiSearchApiResult<AiSearchReadResponse>> => {
  const response = await callApi(
    AI_SEARCH_READ_PATH,
    {
      queryParams: {
        message_id: encodeURIComponent(messageId),
      },
      aiSafeMode: true,
    },
    true,
    false,
    '',
    'GET',
    true
  );

  return parseAiSearchResponse<AiSearchReadResponse>(response);
};
